/**
 * useNavCounts — poll the entity's nav-badge aggregate counts.
 *
 * NS4 Round 3 R3.3. Returns { counts, refresh, loading } where counts is a
 * shape matching api.getNavCounts(). Refetches automatically:
 *   - on mount (after activeEntity resolves)
 *   - every 90 seconds (low traffic; counts don't need real-time)
 *   - on a custom `nexus:nav-counts-refresh` window event so writers (mark
 *     bill paid, approve PTO, etc.) can prompt an immediate update.
 *
 * Returns all-zeros for any caller in local mode or with no active entity.
 *
 * Usage in SideNav:
 *   const { counts } = useNavCounts();
 *   <NavLink ...><NavBadge count={counts.overdueBills} variant="urgent" /></NavLink>
 *
 * To trigger a refresh after an action:
 *   window.dispatchEvent(new Event('nexus:nav-counts-refresh'));
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isInLocalMode } from '@/lib/api';
import { useAppStore } from '@/stores/useAppStore';

export interface NavCounts {
  overdueBills: number;
  pendingApprovals: number;
  pendingPto: number;
  missingW9: number;
  complianceDueSoon: number;
  // NS4 R7 — counsel-review nudge (mig 071) + onboarding nudge (mig 072)
  policiesReviewNeeded?: number;
  onboardingUnstarted?: number;
  /** Sprint 1.5 Layer 2 (2026-05-28). 0-100 MTD % of the entity's AI cap.
   *  Smart Attention surfaces a row at ≥75 (warn) and ≥90 (urgent).
   *  Bar color escalation + in-chat banner + email cron use the same numbers. */
  aiBudgetPct?: number;
  /** Phase 7c (2026-07-12). Scanned-statement OCR pages used this calendar month
   *  + the entity's included allowance. Smart Attention + CardReconcile banner warn
   *  at ≥270 and flag overage at ≥ included (300 default). Metering, never blocks. */
  textractPagesUsed?: number;
  textractPagesIncluded?: number;
}

const EMPTY: NavCounts = {
  overdueBills: 0,
  pendingApprovals: 0,
  pendingPto: 0,
  missingW9: 0,
  complianceDueSoon: 0,
  policiesReviewNeeded: 0,
  onboardingUnstarted: 0,
  aiBudgetPct: 0,
  textractPagesUsed: 0,
  textractPagesIncluded: 300,
};

const POLL_MS = 90_000;
const REFRESH_EVENT = 'nexus:nav-counts-refresh';

export function useNavCounts() {
  const { activeEntity } = useAppStore();
  const [counts, setCounts] = useState<NavCounts>(EMPTY);
  const [loading, setLoading] = useState(false);
  const cancelRef = useRef(false);

  const refresh = useCallback(async () => {
    if (isInLocalMode() || !activeEntity?.id) {
      setCounts(EMPTY);
      return;
    }
    setLoading(true);
    try {
      const r = await api.getNavCounts();
      if (!cancelRef.current) setCounts(r);
    } catch {
      // Non-fatal: badges silently go to zero rather than breaking the
      // sidebar. A real failure shows up in the network tab + browser
      // console; nav badges are a hint, not a blocker.
      if (!cancelRef.current) setCounts(EMPTY);
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [activeEntity?.id]);

  useEffect(() => {
    cancelRef.current = false;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    const onEvent = () => refresh();
    window.addEventListener(REFRESH_EVENT, onEvent);
    return () => {
      cancelRef.current = true;
      clearInterval(id);
      window.removeEventListener(REFRESH_EVENT, onEvent);
    };
  }, [refresh]);

  return { counts, refresh, loading };
}

/** Convenience helper for callers that mutate underlying data. */
export function refreshNavCounts() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(REFRESH_EVENT));
  }
}
